/* =========================================================
   Affiche L'ATTIRAIL — le matériel du sport, étalé et illustré.
   API : SNAtAttirail.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe, l1..l5,
              teinte1, teinte2 }
     opts : { palette }

   CE QUI DISTINGUE CETTE GAMME DE « MON MATÉRIEL ». L'autre dessine UN objet
   à ses cotes réelles, et n'existe que pour les objets définis par des
   nombres — la ligne de cote d'un ski, le diamètre d'une boule. Celle-ci ne
   cote rien : elle ÉTALE l'équipement pour qu'on identifie le sport au premier
   coup d'œil. Une bouteille de plongée n'a aucune cote personnelle à raconter,
   mais posée à côté d'un détendeur, d'un masque et d'une paire de palmes, elle
   dit « plongée » avant même qu'on ait lu le titre.

   Deux teintes au choix du client, prises dans une TABLE FERMÉE : elles
   finissent dans des attributs `fill`, jamais de saisie libre par ce chemin.

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie:   { label:'Craie',   bg:'#F4F1EA', ink:'#22262B', muted:'#8A8D93', line:'#E1DCD1', card:'#FFFFFF' },
    atelier: { label:'Atelier', bg:'#20242A', ink:'#F0F2F3', muted:'#8E979F', line:'#333A43', card:'#2A2F36' },
    nuit:    { label:'Nuit',    bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', line:'#1E4272', card:'#12355F' },
    sable:   { label:'Sable',   bg:'#2B2018', ink:'#F5EFE4', muted:'#B49B7E', line:'#4A392A', card:'#3A2C20' },
  };
  const COULEURS = {
    rouge: '#D64545', corail: '#E8663D', orange: '#E8933D', jaune: '#E8C33D', citron: '#C9D64A',
    vert: '#3FA35C', sapin: '#1F5B3A', turquoise: '#2FB3AE', ciel: '#5BA8D6', bleu: '#2E6DA4',
    marine: '#1B3A5C', violet: '#7B5EA7', rose: '#D96A9A', bordeaux: '#8A2B3A', gris: '#9AA3AB',
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };
  const cleFermee = (v, table, defaut) => {
    const k = String(v == null ? '' : v).trim().toLowerCase().replace(/[\s-]/g, '');
    return Object.prototype.hasOwnProperty.call(table, k) ? k : defaut;
  };
  const teinte = (v, d) => COULEURS[cleFermee(v, COULEURS, d)];

  /* Raccourcis de dessin : tout l'attirail est fait de ces quatre primitives,
     ce qui donne son unité graphique à la gamme. */
  const R = (x, y, w, h, r, f, o) => `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="${r}" fill="${f}"${o ? ` opacity="${o}"` : ''}/>`;
  const C = (x, y, r, f, o) => `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" fill="${f}"${o ? ` opacity="${o}"` : ''}/>`;
  const A = (d, s, w, cap) => `<path d="${d}" fill="none" stroke="${s}" stroke-width="${w}" stroke-linecap="${cap || 'round'}" stroke-linejoin="round"/>`;
  const P = (d, f, s, w) => `<path d="${d}" fill="${f}"${s ? ` stroke="${s}" stroke-width="${w || 2}" stroke-linejoin="round"` : ''}/>`;

  /* =================================================================
     LES ÉTALAGES. Chacun compose son matériel dans la boîte utile
     x 64 → 736, y 262 → 800. Aucune cote, aucun chiffre : ces affiches
     illustrent, elles ne documentent pas.
     ================================================================= */
  const ETALAGES = {
    plongee: {
      label: 'Plongée', titre: 'MON ATTIRAIL', kicker: 'PLONGÉE',
      pieces: ['Bloc', 'Détendeur', 'Masque', 'Palmes', 'Ordinateur'],
      dessin(p, c1, c2) {
        let g = '';
        /* LE BLOC, à gauche : fût, culot, ogive et robinetterie. */
        const bx = 150, by = 330, bw = 74, bh = 340;
        g += P(`M ${bx-bw/2} ${by+bh} L ${bx-bw/2} ${by+bw*0.5} Q ${bx-bw/2} ${by-bw*0.10} ${bx-bw*0.22} ${by-bw*0.22}`
             + ` L ${bx+bw*0.22} ${by-bw*0.22} Q ${bx+bw/2} ${by-bw*0.10} ${bx+bw/2} ${by+bw*0.5} L ${bx+bw/2} ${by+bh} Z`, c1, p.ink, 2.5);
        g += R(bx-bw/2, by+bh-26, bw, 26, 0, p.ink, 0.20)
           + R(bx-bw/2, by+bw*0.42, bw, 16, 0, p.ink, 0.14)
           + R(bx-12, by-bw*0.52, 24, 30, 3, p.ink)
           + R(bx-40, by-bw*0.48, 28, 12, 4, p.ink)
           + C(bx+32, by-bw*0.34, 12, 'none') + A(`M ${bx+20} ${by-bw*0.34} a 12 12 0 1 0 24 0 a 12 12 0 1 0 -24 0`, p.ink, 3.5);

        /* LE DÉTENDEUR : premier étage, tuyaux qui serpentent, deuxième étage
           et manomètre. Ce sont les tuyaux qui font reconnaître l'objet. */
        const dx = 236, dy = 520;
        g += A(`M ${dx} ${dy} C ${dx+70} ${dy+30} ${dx+90} ${dy+90} ${dx+120} ${dy+120}`, p.ink, 9)
           + A(`M ${dx} ${dy} C ${dx-40} ${dy+60} ${dx-30} ${dy+130} ${dx-52} ${dy+168}`, p.ink, 8)
           + R(dx-22, dy-24, 44, 44, 8, p.ink)
           + C(dx, dy-2, 9, c2);
        /* deuxième étage, en bas à droite du premier */
        g += R(dx+96, dy+104, 60, 46, 12, p.ink) + R(dx+104, dy+112, 44, 30, 8, c2)
           + P(`M ${dx+126} ${dy+150} l 14 16 l -28 0 z`, p.ink);
        /* manomètre */
        g += C(dx-52, dy+186, 26, p.ink) + C(dx-52, dy+186, 19, p.bg)
           + A(`M ${dx-64} ${dy+194} L ${dx-46} ${dy+174}`, p.ink, 3);

        /* LE MASQUE. La première version ressemblait à des lunettes de soleil :
           deux verres ronds et séparés sur une monture plate. Un masque de
           plongée, c'est l'inverse — une JUPE large et souple, deux vitres
           trapézoïdales serrées l'une contre l'autre, et surtout un LOGEMENT
           DE NEZ en dessous. C'est lui qui fait toute la différence. */
        const mx = 384, my = 372;
        const jw = 116, jh = 52;
        g += P(`M ${mx-jw} ${my-14} Q ${mx-jw-6} ${my+26} ${mx-jw+18} ${my+40}`
             + ` L ${mx-30} ${my+44} Q ${mx-26} ${my+82} ${mx} ${my+84} Q ${mx+26} ${my+82} ${mx+30} ${my+44}`
             + ` L ${mx+jw-18} ${my+40} Q ${mx+jw+6} ${my+26} ${mx+jw} ${my-14}`
             + ` Q ${mx+jw-14} ${my-jh} ${mx} ${my-jh+2} Q ${mx-jw+14} ${my-jh} ${mx-jw} ${my-14} Z`, p.ink);
        const vitre = sg => P(`M ${mx+sg*14} ${my-30} L ${mx+sg*94} ${my-24} Q ${mx+sg*102} ${my+6} ${mx+sg*84} ${my+22}`
             + ` L ${mx+sg*20} ${my+18} Q ${mx+sg*12} ${my-6} ${mx+sg*14} ${my-30} Z`, c2, '', 0);
        g += vitre(-1) + vitre(1);
        /* la sangle, qui part de chaque boucle et retombe */
        g += A(`M ${mx-jw+4} ${my-10} C ${mx-jw-52} ${my-26} ${mx-jw-64} ${my+26} ${mx-jw-34} ${my+44}`, p.ink, 8)
           + A(`M ${mx+jw-4} ${my-10} C ${mx+jw+52} ${my-26} ${mx+jw+64} ${my+26} ${mx+jw+34} ${my+44}`, p.ink, 8);

        /* LES PALMES : voilure LONGUE et effilée, chausson fermé bien marqué.
           Trop courtes, elles se lisaient comme deux rectangles. */
        const palme = (px, py, inc) => {
          const H = 348, wH = 34, wB = 26;
          const d = `M ${px-wH} ${py+52} Q ${px-wB-10} ${py+180} ${px-wB} ${py+H-24}`
                  + ` Q ${px} ${py+H} ${px+wB} ${py+H-24}`
                  + ` Q ${px+wB+10} ${py+180} ${px+wH} ${py+52} Z`;
          return `<g transform="rotate(${inc} ${px} ${py + H / 2})">`
            + P(d, c1, p.ink, 2.5)
            /* nervures de voilure */
            + A(`M ${px-14} ${py+96} L ${px-11} ${py+H-40}`, p.ink, 2, 'butt')
            + A(`M ${px+14} ${py+96} L ${px+11} ${py+H-40}`, p.ink, 2, 'butt')
            /* le chausson : talon ouvert vers le haut, semelle vers la voilure */
            + P(`M ${px-wH-4} ${py+56} L ${px-wH-4} ${py+8} Q ${px-wH-4} ${py-26} ${px} ${py-26}`
              + ` Q ${px+wH+4} ${py-26} ${px+wH+4} ${py+8} L ${px+wH+4} ${py+56} Z`, p.ink)
            + P(`M ${px-wH+6} ${py+34} L ${px-wH+6} ${py+12} Q ${px-wH+6} ${py-14} ${px} ${py-14}`
              + ` Q ${px+wH-6} ${py-14} ${px+wH-6} ${py+12} L ${px+wH-6} ${py+34} Z`, p.bg)
            + `</g>`;
        };
        g += palme(596, 318, -7) + palme(682, 336, 8);

        /* L'ORDINATEUR de plongée, en bas au centre-gauche. */
        const ox = 356, oy = 742;
        g += R(ox-36, oy-32, 72, 64, 13, p.ink) + R(ox-26, oy-22, 52, 44, 7, c2)
           + A(`M ${ox-36} ${oy-14} L ${ox-70} ${oy-14}`, p.ink, 14)
           + A(`M ${ox+36} ${oy-14} L ${ox+70} ${oy-14}`, p.ink, 14);
        return g;
      },
    },

    escalade: {
      label: 'Escalade', titre: 'MON ATTIRAIL', kicker: 'ESCALADE',
      pieces: ['Chaussons', 'Baudrier', 'Dégaines', 'Corde', 'Magnésie'],
      dessin(p, c1, c2) {
        let g = '';
        /* LA CORDE LOVÉE, en haut à gauche : des anneaux concentriques et deux
           brins qui sortent. C'est l'objet le plus lisible de l'escalade. */
        const rx = 210, ry = 402;
        for (let i = 0; i < 5; i++)
          g += `<ellipse cx="${rx}" cy="${ry}" rx="${(118 - i * 21).toFixed(1)}" ry="${(96 - i * 17).toFixed(1)}" fill="none" stroke="${i % 2 ? c2 : c1}" stroke-width="13"/>`;
        g += A(`M ${rx-104} ${ry+58} C ${rx-140} ${ry+96} ${rx-120} ${ry+130} ${rx-80} ${ry+140}`, c1, 12);

        /* LES CHAUSSONS, en bas à gauche : deux semelles vues de dessous, ce
           qui est bien plus lisible qu'un profil et tout aussi parlant. */
        const chausson = (sx, sy, inc) => `<g transform="rotate(${inc} ${sx} ${sy})">`
          + P(`M ${sx} ${sy-92} C ${sx+34} ${sy-78} ${sx+40} ${sy-30} ${sx+34} ${sy+6}`
            + ` C ${sx+30} ${sy+44} ${sx+34} ${sy+70} ${sx+22} ${sy+86}`
            + ` C ${sx+8} ${sy+100} ${sx-8} ${sy+100} ${sx-22} ${sy+86}`
            + ` C ${sx-34} ${sy+70} ${sx-30} ${sy+44} ${sx-34} ${sy+6}`
            + ` C ${sx-40} ${sy-30} ${sx-34} ${sy-78} ${sx} ${sy-92} Z`, c1, p.ink, 2.5)
          + P(`M ${sx} ${sy-84} C ${sx+28} ${sy-72} ${sx+33} ${sy-30} ${sx+28} ${sy+2}`
            + ` C ${sx+14} ${sy+10} ${sx-14} ${sy+10} ${sx-28} ${sy+2}`
            + ` C ${sx-33} ${sy-30} ${sx-28} ${sy-72} ${sx} ${sy-84} Z`, p.ink, '', 0)
          + P(`M ${sx-24} ${sy+52} C ${sx-10} ${sy+44} ${sx+10} ${sy+44} ${sx+24} ${sy+52}`
            + ` C ${sx+28} ${sy+74} ${sx+14} ${sy+92} ${sx} ${sy+92}`
            + ` C ${sx-14} ${sy+92} ${sx-28} ${sy+74} ${sx-24} ${sy+52} Z`, p.ink, '', 0) + `</g>`;
        g += chausson(160, 664, -11) + chausson(262, 680, 9);

        /* LE BAUDRIER, à droite : ceinture, boucle et deux tours de cuisse. */
        const hx = 552, hy = 372;
        g += A(`M ${hx-96} ${hy} Q ${hx} ${hy-42} ${hx+96} ${hy}`, c1, 20)
           + R(hx-20, hy-30, 40, 26, 5, p.ink)
           + A(`M ${hx-72} ${hy+12} C ${hx-96} ${hy+58} ${hx-56} ${hy+96} ${hx-18} ${hy+72}`, c2, 13)
           + A(`M ${hx+72} ${hy+12} C ${hx+96} ${hy+58} ${hx+56} ${hy+96} ${hx+18} ${hy+72}`, c2, 13)
           + A(`M ${hx-30} ${hy+18} L ${hx+30} ${hy+18}`, p.ink, 8);

        /* LES DÉGAINES : deux mousquetons reliés par une sangle. Trois de
           suite, décalées, comme accrochées au porte-matériel. */
        const mousqueton = (qx, qy, s, col) => {
          const h = 52 * s, w = 30 * s;
          return A(`M ${qx+w*0.30} ${qy-h/2} Q ${qx-w/2} ${qy-h/2} ${qx-w/2} ${qy-h*0.22}`
            + ` L ${qx-w/2} ${qy+h*0.26} Q ${qx-w/2} ${qy+h/2} ${qx-w*0.14} ${qy+h/2}`
            + ` L ${qx+w*0.14} ${qy+h/2} Q ${qx+w/2} ${qy+h/2} ${qx+w/2} ${qy+h*0.26}`
            + ` L ${qx+w/2} ${qy-h*0.14}`, col, 7 * s);
        };
        for (let i = 0; i < 3; i++) {
          const qx = 452 + i * 78, qy = 596;
          g += A(`M ${qx} ${qy+26} L ${qx} ${qy+96}`, i % 2 ? c2 : c1, 17)
            + mousqueton(qx, qy, 1, p.ink) + mousqueton(qx, qy + 122, 1, p.ink);
        }

        /* LE SAC À MAGNÉSIE, en bas à droite. */
        const sx2 = 686, sy2 = 706;
        g += P(`M ${sx2-42} ${sy2-46} L ${sx2+42} ${sy2-46} L ${sx2+36} ${sy2+50}`
             + ` Q ${sx2} ${sy2+66} ${sx2-36} ${sy2+50} Z`, c1, p.ink, 2.5)
           + A(`M ${sx2-42} ${sy2-42} Q ${sx2} ${sy2-64} ${sx2+42} ${sy2-42}`, p.ink, 7)
           + C(sx2, sy2 - 48, 13, p.bg);
        return g;
      },
    },

    ski: {
      label: 'Ski', titre: 'MON ATTIRAIL', kicker: 'SKI',
      pieces: ['Skis', 'Bâtons', 'Masque', 'Casque', 'Gants'],
      /* COMPOSITION EN DEUX COLONNES. La première version faisait pivoter les
         bâtons autour de leur milieu : les pointes s'écartaient de trente-cinq
         unités de part et d'autre et l'ensemble se lisait comme une paire de
         ciseaux posée sur les skis. Ici les longs objets restent groupés à
         gauche, presque verticaux, et la panoplie de tête s'empile à droite. */
      dessin(p, c1, c2) {
        let g = '';

        /* ---- colonne de gauche : les skis, puis les bâtons ---- */
        const ski = (sx, inc) => {
          const h = 448, w = 20, y0 = 296;
          return `<g transform="rotate(${inc} ${sx} ${y0 + h / 2})">`
            + P(`M ${sx} ${y0} Q ${sx+w} ${y0+38} ${sx+w*0.72} ${y0+114}`
              + ` L ${sx+w*0.62} ${y0+h-66} Q ${sx+w*0.9} ${y0+h-13} ${sx+w*0.5} ${y0+h}`
              + ` L ${sx-w*0.5} ${y0+h} Q ${sx-w*0.9} ${y0+h-13} ${sx-w*0.62} ${y0+h-66}`
              + ` L ${sx-w*0.72} ${y0+114} Q ${sx-w} ${y0+38} ${sx} ${y0} Z`, c1, p.ink, 2.5)
            + R(sx - w * 0.85, y0 + 222, w * 1.7, 40, 5, p.ink)
            + `</g>`;
        };
        g += ski(168, -5) + ski(228, 5);

        /* Les bâtons restent DROITS et serrés : c'est leur rondelle et leur
           poignée qui les identifient, pas leur inclinaison. */
        const baton = bx => A(`M ${bx} 306 L ${bx} 742`, p.ink, 6)
          + R(bx - 9, 300, 18, 50, 8, c2)
          + C(bx, 702, 17, 'none') + A(`M ${bx-17} 702 a 17 17 0 1 0 34 0 a 17 17 0 1 0 -34 0`, p.ink, 4.5);
        g += baton(324) + baton(366);

        /* ---- colonne de droite : casque, masque, gants ---- */
        const kx = 588, ky = 372;
        g += P(`M ${kx-84} ${ky+38} Q ${kx-88} ${ky-48} ${kx} ${ky-52} Q ${kx+88} ${ky-48} ${kx+84} ${ky+38}`
             + ` Q ${kx+46} ${ky+54} ${kx} ${ky+52} Q ${kx-46} ${ky+54} ${kx-84} ${ky+38} Z`, c1, p.ink, 2.5)
           + A(`M ${kx-82} ${ky+18} Q ${kx} ${ky+4} ${kx+82} ${ky+18}`, p.ink, 4)
           + C(kx - 62, ky + 38, 12, p.ink)
           /* la jugulaire, refermée sous la coque */
           + A(`M ${kx-70} ${ky+46} Q ${kx} ${ky+92} ${kx+70} ${ky+46}`, p.ink, 6);

        /* Le masque : large, mousse épaisse, écran teinté et sangle qui part
           des deux côtés — la pièce la plus reconnaissable de la panoplie. */
        const mx = 588, my = 566;
        g += P(`M ${mx-104} ${my-22} Q ${mx-112} ${my+34} ${mx-56} ${my+44}`
             + ` Q ${mx} ${my+52} ${mx+56} ${my+44} Q ${mx+112} ${my+34} ${mx+104} ${my-22}`
             + ` Q ${mx+88} ${my-58} ${mx} ${my-56} Q ${mx-88} ${my-58} ${mx-104} ${my-22} Z`, p.ink);
        g += P(`M ${mx-88} ${my-18} Q ${mx-93} ${my+22} ${mx-51} ${my+30}`
             + ` Q ${mx} ${my+37} ${mx+51} ${my+30} Q ${mx+93} ${my+22} ${mx+88} ${my-18}`
             + ` Q ${mx+74} ${my-42} ${mx} ${my-40} Q ${mx-74} ${my-42} ${mx-88} ${my-18} Z`, c2);
        g += A(`M ${mx-104} ${my-20} L ${mx-146} ${my-26}`, p.ink, 14)
           + A(`M ${mx+104} ${my-20} L ${mx+146} ${my-26}`, p.ink, 14);

        /* Les gants, PAR PAIRE — il n'y en avait qu'un. Pouces opposés, comme
           une vraie paire posée à plat. */
        const gant = (gx, gy, sg, inc) => `<g transform="rotate(${inc} ${gx} ${gy})">`
          + P(`M ${gx-sg*34} ${gy+46} L ${gx-sg*34} ${gy-16} Q ${gx-sg*34} ${gy-48} ${gx-sg*7} ${gy-48}`
            + ` Q ${gx+sg*19} ${gy-48} ${gx+sg*19} ${gy-16} L ${gx+sg*19} ${gy+4}`
            + ` Q ${gx+sg*46} ${gy+2} ${gx+sg*46} ${gy+23} L ${gx+sg*42} ${gy+46} Z`, c1, p.ink, 2.5)
          + R(Math.min(gx - sg * 36, gx + sg * 44), gy + 42, 80, 18, 6, p.ink) + `</g>`;
        g += gant(528, 726, 1, -8) + gant(662, 736, -1, 9);
        return g;
      },
    },
    surf: {
      label: 'Surf', titre: 'MON ATTIRAIL', kicker: 'SURF',
      pieces: ['Planche', 'Combinaison', 'Leash', 'Wax', 'Dérive'],
      dessin(p, c1, c2) {
        let g = '';
        /* LA PLANCHE, à gauche, nose en haut. Contour en deux Béziers miroir,
           stringer, plugs de dérives. */
        const px = 166, y0 = 288, h = 476, hw = 54, cul = hw * 0.44;
        const Pt = (sg, x, y) => `${(px + sg * x).toFixed(1)} ${y.toFixed(1)}`;
        const a1 = [hw * 0.22, y0 + h * 0.13], a2 = [hw, y0 + h * 0.32], a3 = [hw, y0 + h * 0.52];
        const b1 = [hw, y0 + h * 0.74], b2 = [cul + (hw - cul) * 0.55, y0 + h * 0.90], b3 = [cul, y0 + h];
        g += P(`M ${px} ${y0} C ${Pt(1,a1[0],a1[1])} ${Pt(1,a2[0],a2[1])} ${Pt(1,a3[0],a3[1])}`
             + ` C ${Pt(1,b1[0],b1[1])} ${Pt(1,b2[0],b2[1])} ${Pt(1,b3[0],b3[1])}`
             + ` L ${Pt(1,cul*0.60,y0+h)} Q ${px} ${(y0+h-cul*0.5).toFixed(1)} ${Pt(-1,cul*0.60,y0+h)} L ${Pt(-1,cul,y0+h)}`
             + ` C ${Pt(-1,b2[0],b2[1])} ${Pt(-1,b1[0],b1[1])} ${Pt(-1,a3[0],a3[1])}`
             + ` C ${Pt(-1,a2[0],a2[1])} ${Pt(-1,a1[0],a1[1])} ${px} ${y0} Z`, c1, p.ink, 2.5)
           + A(`M ${px} ${(y0 + 12).toFixed(1)} L ${px} ${(y0 + h - 22).toFixed(1)}`, p.ink, 2, 'butt')
           + `<ellipse cx="${px}" cy="${(y0 + h * 0.955).toFixed(1)}" rx="6" ry="11" fill="${p.ink}"/>`
           + `<ellipse cx="${(px - hw * 0.52).toFixed(1)}" cy="${(y0 + h * 0.91).toFixed(1)}" rx="6" ry="11" fill="${p.ink}"/>`
           + `<ellipse cx="${(px + hw * 0.52).toFixed(1)}" cy="${(y0 + h * 0.91).toFixed(1)}" rx="6" ry="11" fill="${p.ink}"/>`;

        /* LA COMBINAISON, étalée à plat : encolure, manches écartées, jambes. */
        const wx = 578, wy = 300;
        g += P(`M ${wx-34} ${wy} Q ${wx} ${wy+26} ${wx+34} ${wy}`
             + ` L ${wx+60} ${wy+8} L ${wx+118} ${wy+128} L ${wx+92} ${wy+150} L ${wx+62} ${wy+92}`
             + ` L ${wx+56} ${wy+236} L ${wx+50} ${wy+396} L ${wx+14} ${wy+396} L ${wx+4} ${wy+250}`
             + ` L ${wx-6} ${wy+396} L ${wx-42} ${wy+396} L ${wx-48} ${wy+236}`
             + ` L ${wx-54} ${wy+92} L ${wx-84} ${wy+150} L ${wx-110} ${wy+128} L ${wx-52} ${wy+8} Z`, c2, p.ink, 2.5)
           + A(`M ${wx-34} ${wy+2} Q ${wx} ${wy+30} ${wx+34} ${wy+2}`, p.ink, 5)
           + R(wx - 50, wy + 150, 100, 12, 4, p.ink, 0.35)
           /* la fermeture dorsale, avec sa tirette */
           + A(`M ${wx} ${wy+22} L ${wx} ${wy+150}`, p.ink, 3, 'butt') + C(wx, wy + 154, 8, p.ink);

        /* LE LEASH, lové, avec sa manchette de cheville. */
        const lx = 372, ly = 402;
        for (let i = 0; i < 3; i++)
          g += `<ellipse cx="${lx}" cy="${ly}" rx="${(72 - i * 20).toFixed(1)}" ry="${(58 - i * 16).toFixed(1)}" fill="none" stroke="${p.ink}" stroke-width="9"/>`;
        g += A(`M ${lx-70} ${ly+34} C ${lx-96} ${ly+70} ${lx-72} ${ly+96} ${lx-42} ${ly+98}`, p.ink, 9)
           + R(lx - 46, ly + 84, 46, 26, 8, c2);

        /* LA WAX, un pain posé de trois quarts. */
        const kx = 372, ky = 600;
        g += P(`M ${kx-52} ${ky-24} L ${kx+52} ${ky-24} L ${kx+44} ${ky+26} L ${kx-44} ${ky+26} Z`, c2, p.ink, 2.5)
           + A(`M ${kx-38} ${ky-4} L ${kx+38} ${ky-4}`, p.ink, 3, 'butt')
           + A(`M ${kx-34} ${ky+12} L ${kx+34} ${ky+12}`, p.ink, 3, 'butt');

        /* LA DÉRIVE centrale, plantée sur sa base. */
        const fx = 372, fy = 748;
        g += P(`M ${fx-46} ${fy+30} L ${fx-30} ${fy-46} Q ${fx+6} ${fy-56} ${fx+26} ${fy-10}`
             + ` Q ${fx+42} ${fy+20} ${fx+34} ${fy+30} Z`, c1, p.ink, 2.5);
        return g;
      },
    },

    petanque: {
      label: 'Pétanque', titre: 'MON ATTIRAIL', kicker: 'PÉTANQUE',
      pieces: ['Trois boules', 'Le but'],
      /* Le mètre ruban et le chiffon ont été retirés : dessinés petits, l'un
         se lisait comme un beignet percé, l'autre comme une feuille. La
         pétanque n'a de toute façon que deux objets, et ce sont les bons —
         trois boules et un but, à leur vrai écart de taille. C'est ce
         rapport-là qui fait sourire. */
      dessin(p, c1, c2) {
        /* Striage FIN et serré : neuf cordes calculées plutôt que six larges.
           Des stries épaisses aplatissaient la boule au lieu de la creuser. */
        const boule = (bx, by, R, col) => {
          let s = C(bx, by, R, col) + `<circle cx="${bx}" cy="${by}" r="${R}" fill="none" stroke="${p.ink}" stroke-width="3"/>`;
          for (let i = 1; i <= 9; i++) {
            const k = -1 + (2 * i) / 10;
            const y = by + k * R, hw = Math.sqrt(Math.max(0, 1 - k * k)) * R * 0.975;
            s += A(`M ${(bx - hw).toFixed(1)} ${y.toFixed(1)} L ${(bx + hw).toFixed(1)} ${y.toFixed(1)}`, p.ink, R * 0.034, 'butt');
          }
          /* le point de lumière, qui donne le volume */
          return s + C(bx - R * 0.36, by - R * 0.38, R * 0.20, '#FFFFFF', 0.17);
        };
        const R = 112;
        let g = boule(298, 424, R, c1) + boule(504, 486, R, c1) + boule(384, 646, R, c1);
        /* LE BUT, à sa vraie petitesse : trente millimètres contre soixante-quinze. */
        g += C(608, 682, R * 0.20, c2)
           + `<circle cx="608" cy="682" r="${(R * 0.20).toFixed(1)}" fill="none" stroke="${p.ink}" stroke-width="2.5"/>`
           + C(608 - R * 0.07, 682 - R * 0.07, R * 0.05, '#FFFFFF', 0.20);
        return g;
      },
    },
    aviron: {
      label: 'Aviron', titre: 'MON ATTIRAIL', kicker: 'AVIRON',
      pieces: ['Pelle', 'Combinaison', 'Chaussures', 'Chronomètre'],
      dessin(p, c1, c2) {
        let g = '';
        /* LA PELLE, à gauche : manche haut, hachoir en bas aux couleurs. */
        const px = 168, hP = 250, wP = 116, yP = 470, yB = yP + hP;
        g += R(px - 10, 296, 20, yP - 286, 10, p.bg) + R(px - 10, 296, 20, yP - 286, 10, c2, 0)
           + `<rect x="${px-10}" y="296" width="20" height="${(yP-286).toFixed(1)}" rx="10" fill="${p.bg}" stroke="${p.ink}" stroke-width="2.5"/>`;
        g += P(`M ${px} ${yP} C ${px+wP*0.34} ${yP+6} ${px+wP*0.5} ${yP+18} ${px+wP*0.5} ${yP+42}`
             + ` L ${px+wP*0.5} ${yB-14} Q ${px+wP*0.5} ${yB} ${px+wP*0.34} ${yB}`
             + ` L ${px-wP*0.42} ${yB-28} Q ${px-wP*0.5} ${yB-34} ${px-wP*0.5} ${yB-56}`
             + ` L ${px-wP*0.5} ${yP+62} C ${px-wP*0.5} ${yP+18} ${px-wP*0.34} ${yP+6} ${px} ${yP} Z`, c1, p.ink, 2.5);
        g += R(px - wP * 0.5, yP + hP * 0.34, wP, hP * 0.15, 0, c2)
           + R(px - wP * 0.5, yP + hP * 0.62, wP, hP * 0.15, 0, c2)
           + R(px - 13, yP - 62, 26, 16, 4, p.ink);

        /* LA COMBINAISON une pièce, bretelles et jambes courtes. */
        const wx = 470, wy = 316;
        g += P(`M ${wx-56} ${wy+70} L ${wx-44} ${wy} L ${wx-16} ${wy+8} L ${wx-14} ${wy+52}`
             + ` L ${wx+14} ${wy+52} L ${wx+16} ${wy+8} L ${wx+44} ${wy} L ${wx+56} ${wy+70}`
             + ` L ${wx+62} ${wy+206} L ${wx+56} ${wy+300} L ${wx+12} ${wy+300} L ${wx+4} ${wy+214}`
             + ` L ${wx-4} ${wy+300} L ${wx-48} ${wy+300} L ${wx-54} ${wy+206} Z`, c1, p.ink, 2.5)
           + R(wx - 58, wy + 132, 116, 26, 0, c2)
           + A(`M ${wx-14} ${wy+52} L ${wx+14} ${wy+52}`, p.ink, 3);

        /* LES CHAUSSURES de bateau, vissées sur la planche de pieds. */
        const chaussure = (sx, sy, sg) => P(`M ${sx-sg*40} ${sy+26} L ${sx-sg*36} ${sy-16}`
          + ` Q ${sx-sg*32} ${sy-40} ${sx-sg*6} ${sy-40} Q ${sx+sg*22} ${sy-40} ${sx+sg*30} ${sy-8}`
          + ` L ${sx+sg*40} ${sy+16} Q ${sx+sg*42} ${sy+30} ${sx+sg*26} ${sy+30} Z`, c2, p.ink, 2.5)
          + A(`M ${sx-sg*28} ${sy-12} L ${sx+sg*16} ${sy-6}`, p.ink, 3);
        g += chaussure(632, 458, 1) + chaussure(632, 552, -1);

        /* LE CHRONOMÈTRE, en bas à droite. */
        const kx = 630, ky = 700;
        g += C(kx, ky, 58, p.ink) + C(kx, ky, 47, p.bg)
           + R(kx - 13, ky - 72, 26, 18, 5, p.ink)
           + A(`M ${kx} ${ky-30} L ${kx} ${ky}`, p.ink, 5) + A(`M ${kx} ${ky} L ${kx+26} ${ky+12}`, c1, 5)
           + C(kx, ky, 6, p.ink);
        return g;
      },
    },

    tiralarc: {
      label: 'Tir à l’arc', titre: 'MON ATTIRAIL', kicker: 'TIR À L’ARC',
      pieces: ['Arc', 'Flèches', 'Carquois', 'Blason'],
      /* La palette et le protège-bras ont été retirés : deux petites pièces de
         cuir sans forme franche, qui ne faisaient que des taches rouges. Même
         constat que pour le mousqueton en héros — un petit objet organique ne
         se lit pas. Le blason les remplace : c'est le seul objet du tir à
         l'arc que tout le monde identifie au premier regard. */
      dessin(p, c1, c2) {
        let g = '';
        /* L'ARC bandé. La poupée part LÉGÈREMENT DERRIÈRE la corde avant de
           repartir vers l'avant : ce crochet est le seul détail qui distingue
           un arc classique d'un arc droit sur une silhouette. */
        const ax = 178, ay = 486, h = 208;
        const branche = sg => `M ${ax} ${(ay + sg * h).toFixed(1)}`
          + ` C ${ax - 17} ${(ay + sg * h * 0.95).toFixed(1)} ${ax + 72} ${(ay + sg * h * 0.86).toFixed(1)} ${ax + 104} ${(ay + sg * h * 0.28).toFixed(1)}`;
        g += A(branche(-1), c1, 12) + A(branche(1), c1, 12)
           + `<rect x="${ax + 96}" y="${(ay - h * 0.28).toFixed(1)}" width="22" height="${(h * 0.56).toFixed(1)}" rx="11" fill="${p.ink}"/>`
           + A(`M ${ax} ${(ay - h).toFixed(1)} L ${ax} ${(ay + h).toFixed(1)}`, p.ink, 3)
           /* le viseur, en potence */
           + A(`M ${ax + 106} ${(ay - h * 0.12).toFixed(1)} L ${ax + 162} ${(ay - h * 0.12).toFixed(1)}`, p.ink, 5)
           + C(ax + 166, ay - h * 0.12, 12, 'none')
           + A(`M ${ax + 154} ${(ay - h * 0.12).toFixed(1)} a 12 12 0 1 0 24 0 a 12 12 0 1 0 -24 0`, p.ink, 3.5);

        /* LE CARQUOIS et ses flèches, à droite. */
        const qx = 578, qy = 520;
        for (let i = 0; i < 3; i++) {
          const fx = qx - 44 + i * 44, ft = 226 - i * 16;
          g += A(`M ${fx} ${qy - ft} L ${fx} ${qy + 56}`, p.ink, 5)
            + P(`M ${fx} ${(qy - ft).toFixed(1)} l 15 24 l -15 8 z`, i % 2 ? c2 : c1)
            + P(`M ${fx} ${(qy - ft).toFixed(1)} l -15 24 l 15 8 z`, i % 2 ? c2 : c1);
        }
        g += P(`M ${qx-70} ${qy+28} L ${qx+70} ${qy+28} L ${qx+57} ${qy+192} Q ${qx} ${qy+210} ${qx-57} ${qy+192} Z`, c1, p.ink, 2.5)
           + A(`M ${qx-68} ${qy+52} L ${qx+68} ${qy+52}`, p.ink, 4)
           + A(`M ${qx+64} ${qy+42} C ${qx+114} ${qy+92} ${qx+108} ${qy+156} ${qx+60} ${qy+178}`, p.ink, 7);

        /* LE BLASON, aux couleurs de World Archery. Dessiné du plus GRAND
           disque au plus petit : l'ordre inverse recouvrirait tout de blanc,
           l'erreur déjà commise sur l'affiche « Le blason ». */
        const bx = 398, by = 736, R = 84;
        const ZONES = ['#FFFFFF', '#FFFFFF', '#1D1D1B', '#1D1D1B', '#3FBCE8', '#3FBCE8', '#F1443A', '#F1443A', '#FFD800', '#FFD800'];
        for (let n = 1; n <= 10; n++)
          g += `<circle cx="${bx}" cy="${by}" r="${(R * (11 - n) / 10).toFixed(1)}" fill="${ZONES[n - 1]}" stroke="${n === 3 || n === 4 ? '#FFFFFF' : '#1D1D1B'}" stroke-width="1.4"/>`;
        return g;
      },
    },
  };

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const id = Object.prototype.hasOwnProperty.call(ETALAGES, String(data.mode || '').toLowerCase())
      ? String(data.mode).toLowerCase() : 'plongee';
    const E = ETALAGES[id];
    const cx = PW / 2;
    const c1 = teinte(data.teinte1, id === 'plongee' ? 'turquoise' : id === 'escalade' ? 'corail' : 'bleu');
    const c2 = teinte(data.teinte2, id === 'plongee' ? 'orange' : id === 'escalade' ? 'turquoise' : 'jaune');

    const titre = up(data.titre) || E.titre;
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || E.kicker;
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = E.dessin(p, c1, c2);
    /* La liste des pièces, discrète, sous l'étalage : elle nomme ce qu'on voit
       sans rien coter. */
    corps += T(cx, 838, 11, p.muted, E.pieces.map(up).join('  ·  '), { w:600, ls:2.6 });

    const l = [];
    for (let i = 1; i <= 5; i++) {
      const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
      const [lab, v] = raw.split('|').map(x => (x || '').trim());
      if (lab && v) l.push([lab, v]);
    }
    if (l.length) {
      const y0 = 884, pas = Math.min(32, 128 / Math.max(1, l.length - 1 || 1));
      l.forEach((r, i) => {
        const y = y0 + i * pas;
        corps += T(MX, y, 12, p.muted, esc(up(r[0])), { w:600, ls:2.2, a:'start' })
          + T(PW - MX, y + 3, fit(r[1], 400, 20, 0.58, 0, 11), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
        if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 4).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 4).toFixed(1)}" stroke="${p.line}" stroke-width="1.2"/>`;
      });
    }

    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, c1, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${bas ? T(cx, 1042, fit(bas, 690, 18, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNAtAttirail = { render, PALETTES, ETALAGES, COULEURS };
})();
